import type { MutableRefObject } from "react";
import {
  CollectionBuildSessionList,
  CollectionBuildSessionSummary,
} from "../../app/api/collectionBuild";
import { collectionBuildSummaryTitle } from "./collectionBuildSession";

export const sortCollectionBuildSummariesByUpdatedAt = (
  items: CollectionBuildSessionSummary[]
) =>
  [...items].sort(
    (left, right) =>
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
  );

export const mergeCollectionBuildSessionList = (
  prev: CollectionBuildSessionSummary[],
  result: CollectionBuildSessionList | undefined
): CollectionBuildSessionSummary[] => {
  if (!result) {
    return prev;
  }
  const priorById = new Map(prev.map(item => [item.sessionId, item] as const));
  const items = (result.items || []).map(item => {
    const prior = priorById.get(item.sessionId);
    const theme = item.theme || prior?.theme || null;
    const status =
      item.status === "generating" ||
      (prior?.status === "generating" && item.status !== "generated")
        ? "generating"
        : item.status;
    const apiUpdatedAt = item.updatedAt || item.createdAt || "";
    const priorUpdatedAt = prior?.updatedAt || "";
    const preferLocal =
      Boolean(prior) &&
      new Date(priorUpdatedAt).getTime() > new Date(apiUpdatedAt).getTime();
    const market = preferLocal ? prior?.market ?? item.market : item.market;
    const category = preferLocal
      ? prior?.category ?? item.category
      : item.category;
    const season = preferLocal ? prior?.season ?? item.season : item.season;
    const nextTheme = preferLocal ? prior?.theme || theme : theme;
    const titleBase = {
      title: item.title,
      market,
      category,
      season,
      theme: nextTheme,
      lookCount: item.lookCount,
      moodboardCount: item.moodboardCount,
      status,
    };
    return {
      ...item,
      market,
      category,
      season,
      status,
      theme: nextTheme,
      title: preferLocal
        ? prior?.title || collectionBuildSummaryTitle(titleBase)
        : collectionBuildSummaryTitle(titleBase),
      updatedAt: preferLocal ? priorUpdatedAt : apiUpdatedAt,
      previewUrl: item.previewUrl || prior?.previewUrl || null,
    };
  });
  const apiIds = new Set(items.map(item => item.sessionId));
  const localOnly = prev.filter(item => !apiIds.has(item.sessionId));
  return sortCollectionBuildSummariesByUpdatedAt([...items, ...localOnly]);
};

export type CollectionBuildPendingSyncRefs = {
  pendingRef: MutableRefObject<number[]>;
  awaitingFreshOutputRef: MutableRefObject<Set<number>>;
  seenStatusRef: MutableRefObject<Map<number, string>>;
};

export const pendingIdsFromSessionList = (
  result: CollectionBuildSessionList | undefined,
  refs: CollectionBuildPendingSyncRefs
): number[] => {
  const items = result?.items || [];
  const generatingIds = items
    .filter(item => item.status === "generating")
    .map(item => item.sessionId);
  const statusById = new Map(items.map(item => [item.sessionId, item.status]));
  return Array.from(
    new Set([...refs.pendingRef.current, ...generatingIds])
  ).filter(id => {
    const status = statusById.get(id);
    return (
      status == null ||
      status === "generating" ||
      (status === "failed" && refs.pendingRef.current.includes(id)) ||
      refs.awaitingFreshOutputRef.current.has(id) ||
      refs.seenStatusRef.current.get(id) === "generating"
    );
  });
};
